import { captureException, Sentry } from './sentry';

/**
 * Centralized logging service that sends errors to Sentry
 * while maintaining console output for development.
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *
 *   logger.debug('Debugging info', { userId: '123' });
 *   logger.info('User logged in', { email: user.email });
 *   logger.warn('API rate limit approaching', { remaining: 10 });
 *   logger.error('Failed to save story', error, { storyId: '456' });
 */
export const logger = {
  /**
   * Debug level - only logs in development mode
   */
  debug: (message: string, context?: Record<string, unknown>) => {
    if (import.meta.env.DEV) {
      console.log(`[DEBUG] ${message}`, context ?? '');
    }
  },

  /**
   * Info level - general information
   */
  info: (message: string, context?: Record<string, unknown>) => {
    console.log(`[INFO] ${message}`, context ?? '');
  },

  /**
   * Warning level - potential issues that don't break functionality
   * Adds a breadcrumb to Sentry for context in future errors
   */
  warn: (message: string, context?: Record<string, unknown>) => {
    console.warn(`[WARN] ${message}`, context ?? '');
    Sentry.addBreadcrumb({
      level: 'warning',
      message,
      data: context,
    });
  },

  /**
   * Error level - something went wrong
   * Logs to console AND sends to Sentry for monitoring
   */
  error: (message: string, error?: Error | unknown, context?: Record<string, unknown>) => {
    console.error(`[ERROR] ${message}`, error ?? '', context ?? '');

    if (error instanceof Error) {
      captureException(error, { message, ...context });
    } else if (error) {
      // If error is not an Error instance, capture as a message with context
      Sentry.captureMessage(message, {
        level: 'error',
        extra: { error, ...context },
      });
    } else {
      // No error object provided, just capture the message
      Sentry.captureMessage(message, {
        level: 'error',
        extra: context,
      });
    }
  },
};
